import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { FormikErrors, useFormik } from 'formik'
import produce from 'immer'
import { Button } from 'primereact/button'
import { Calendar } from 'primereact/calendar'
import { Dialog } from 'primereact/dialog'
import { Divider } from 'primereact/divider'
import { Dropdown } from 'primereact/dropdown'
import {
  InputNumber,
  InputNumberValueChangeEvent,
} from 'primereact/inputnumber'
import { InputText } from 'primereact/inputtext'
import { Tooltip } from 'primereact/tooltip'
import { classNames } from 'primereact/utils'

import { DateTemplate } from '../../../../../../components/DateTemplate'
import { StatusTag } from '../../../../../../components/tableTemplates'
import { useToastContext } from '../../../../../../contexts'
import {
  DespatchAdvice,
  DespatchAdviceItem,
  PatchRequest,
  WebEdiDto,
} from '../../../../../../data/models'
import { useSelectFirstAndOnlyOption } from '../../../../../../hooks'
import { getHandleableError } from '../../../../../../utils/errors/isXiaError'
import { calculateBreakpointWidth } from '../../../../../../utils/helper/calculateBreakpointWidth'
import {
  ComparisonArray,
  areItemsModified,
  isOrderModified,
} from '../../../../../../utils/helper/checkConfirmationCodes'
import { delay } from '../../../../../../utils/helper/delay'
import {
  FieldToValidate,
  validateFormikFields,
} from '../../../../../../utils/helper/validateFormikFields'
import { ConfigurableComponent } from '../../../../../../utils/wrapper'
import { useConfigContext } from '../../../../../ui-config'
import {
  requestPatchDespatchAdviceById,
  requestPutDespatchAdviceItemById,
  useAllDespatchAdviceItems,
  useDespatchAdviceById,
  useDespatchAdviceItemById,
} from '../../../../data'

interface Props {
  visible: boolean
  setVisible: (params: boolean) => void
  id: WebEdiDto['Id']
  itemId: DespatchAdviceItem['ItemId']
  setDisplayRejectItem: (params: boolean) => void
  setSelectedItems: any
}

interface FormValues {
  confirmationItemId: DespatchAdviceItem['ConfirmationItemId']
  confirmationCountryOfOrigin: DespatchAdviceItem['ConfirmationCountryOfOrigin']
  confirmationSellerProductId: DespatchAdviceItem['ConfirmationSellerProductId']
  confirmationSellerProductDescription: DespatchAdviceItem['ConfirmationSellerProductDescription']
  confirmationQuantity?: DespatchAdviceItem['ConfirmationQuantity']
  deliveryStatus?: DespatchAdviceItem['DeliveryStatus']
  confirmationUnit?: DespatchAdviceItem['ConfirmationUnit']
  confirmationPriceBaseQuantity?: DespatchAdviceItem['ConfirmationPriceBaseQuantity']
  confirmationPriceUnitOfMeasure?: DespatchAdviceItem['ConfirmationPriceUnitOfMeasure']
  confirmationPrice?: DespatchAdviceItem['ConfirmationPrice']
  confirmationPriceSum?: DespatchAdviceItem['ConfirmationPriceSum']
  confirmationAllowanceChargeSum?: DespatchAdviceItem['ConfirmationAllowanceChargeSum']
  confirmationLineItemAmount?: DespatchAdviceItem['ConfirmationLineItemAmount']
  confirmationDeliveryDate?: Date
}

type FormFieldName = keyof FormValues

export const Edit: React.FC<Props> = ({
  visible,
  setVisible,
  id,
  itemId,
  setDisplayRejectItem,
  setSelectedItems,
}) => {
  const { t, i18n } = useTranslation()
  const { language } = i18n

  const { uiConfig: config } = useConfigContext()

  const { data: despatchAdvice } = useDespatchAdviceById(id)
  const { data: despatchAdviceItems } = useAllDespatchAdviceItems(id)
  const { data: despatchAdviceItem, isLoading } = useDespatchAdviceItemById(
    id,
    itemId!,
  )

  const [confirmationCode, setConfirmationCode] =
    useState<DespatchAdviceItem['ConfirmationCode']>(undefined)

  useEffect(() => {
    if (despatchAdviceItem) {
      setConfirmationCode(despatchAdviceItem.ConfirmationCode)
    }
  }, [despatchAdviceItem])

  const currency = despatchAdvice?.Payload.OrderCurrency

  // const { mutate: globalMutate } = useSWRConfig()

  const { showToast } = useToastContext()
  const [isRequestRunning, setIsRequestRunning] = useState<boolean>(false)
  const [isCalculating, setIsCalculating] = useState(false)

  // ************************************************************
  // formik
  // ************************************************************
  const formik = useFormik<FormValues>({
    enableReinitialize: true,
    initialValues: {
      confirmationItemId: despatchAdviceItem?.ConfirmationItemId || '',
      confirmationCountryOfOrigin:
        despatchAdviceItem?.ConfirmationCountryOfOrigin || '',
      confirmationSellerProductId:
        despatchAdviceItem?.ConfirmationSellerProductId || '',
      confirmationSellerProductDescription:
        despatchAdviceItem?.ConfirmationSellerProductDescription || '',
      confirmationQuantity: despatchAdviceItem?.ConfirmationQuantity || 0,
      deliveryStatus: despatchAdviceItem?.DeliveryStatus || 'NOT_SET',
      confirmationUnit: despatchAdviceItem?.ConfirmationUnit || '',
      confirmationPriceBaseQuantity:
        despatchAdviceItem?.ConfirmationPriceBaseQuantity || 0,
      confirmationPriceUnitOfMeasure:
        despatchAdviceItem?.ConfirmationPriceUnitOfMeasure || '',
      confirmationPrice: despatchAdviceItem?.ConfirmationPrice || 0,
      confirmationPriceSum: despatchAdviceItem?.ConfirmationPriceSum || 0,
      confirmationAllowanceChargeSum:
        despatchAdviceItem?.ConfirmationAllowanceChargeSum || 0,
      confirmationLineItemAmount:
        despatchAdviceItem?.ConfirmationLineItemAmount || 0,
      confirmationDeliveryDate: despatchAdviceItem?.ConfirmationDeliveryDate
        ? new Date(despatchAdviceItem?.ConfirmationDeliveryDate)
        : new Date(),
    },
    validate: data => {
      let errors: FormikErrors<FormValues> = {}

      const fieldsToValidate: FieldToValidate[] = [
        {
          fieldName: 'confirmationItemId',
          config:
            config.despatchAdvice.editDespatchAdviceItem.confirmationItemId,
        },
        {
          fieldName: 'confirmationCountryOfOrigin',
          config:
            config.despatchAdvice.editDespatchAdviceItem
              .confirmationCountryOfOrigin,
        },
        {
          fieldName: 'confirmationSellerProductId',
          config:
            config.despatchAdvice.editDespatchAdviceItem
              .confirmationSellerProductId,
        },
        {
          fieldName: 'confirmationSellerProductDescription',
          config:
            config.despatchAdvice.editDespatchAdviceItem
              .confirmationSellerProductDescription,
        },
        {
          fieldName: 'confirmationQuantity',
          config:
            config.despatchAdvice.editDespatchAdviceItem.confirmationQuantity,
        },
        {
          fieldName: 'deliveryStatus',
          config: config.despatchAdvice.editDespatchAdviceItem.deliveryStatus,
        },
        {
          fieldName: 'confirmationUnit',
          config: config.despatchAdvice.editDespatchAdviceItem.confirmationUnit,
        },
        {
          fieldName: 'confirmationPriceBaseQuantity',
          config:
            config.despatchAdvice.editDespatchAdviceItem
              .confirmationPriceBaseQuantity,
        },
        {
          fieldName: 'confirmationPriceUnitOfMeasure',
          config:
            config.despatchAdvice.editDespatchAdviceItem
              .confirmationPriceUnitOfMeasure,
        },
        {
          fieldName: 'confirmationPrice',
          config:
            config.despatchAdvice.editDespatchAdviceItem.confirmationPrice,
        },
        {
          fieldName: 'confirmationPriceSum',
          config:
            config.despatchAdvice.editDespatchAdviceItem.confirmationPriceSum,
        },
        {
          fieldName: 'confirmationAllowanceChargeSum',
          config:
            config.despatchAdvice.editDespatchAdviceItem
              .confirmationAllowanceChargeSum,
        },
        {
          fieldName: 'confirmationLineItemAmount',
          config:
            config.despatchAdvice.editDespatchAdviceItem
              .confirmationLineItemAmount,
        },
        {
          fieldName: 'confirmationDeliveryDate',
          config:
            config.despatchAdvice.editDespatchAdviceItem
              .confirmationDeliveryDate,
        },
      ]

      validateFormikFields(fieldsToValidate, data, errors)

      // check deliveryStatus
      if (getOpenQuantity() > 0) {
        if (!data.deliveryStatus || data.deliveryStatus === 'NOT_SET') {
          errors.deliveryStatus = t('common.errors.required')
        }
      }

      return errors
    },
    onSubmit: async formData => {
      // TODO: onsubmit überall gleich aufbauen
      // deactivate the buttons
      setIsRequestRunning(true)

      try {
        // close the dialog and reset
        handleClose()

        // submit form
        await submitForm(formData)

        // mutate()

        // show success toast
        showToast({
          style: {},
          closable: true,
          severity: 'success',
          summary: t('common.toasts.editItemSummary'),
          detail: t('common.toasts.editItemDetail', {
            position: despatchAdviceItem?.OrderPosition,
          }),
        })
      } catch (error) {
        let handleableError = getHandleableError(error)
        let errorMessage = handleableError.message

        // show error toast
        showToast({
          style: {},
          closable: true,
          severity: 'error',
          sticky: true,
          summary: t('common.toasts.editItemFailedSummary'),
          detail: errorMessage,
        })
      } finally {
        setSelectedItems([]) // deselect the items in the table
        setIsRequestRunning(false)
      }
    },
  })

  const isFormFieldValid = (name: FormFieldName) =>
    !(formik.touched[name] && formik.errors[name])

  const getFormErrorMessage = (name: FormFieldName) => {
    return (
      !isFormFieldValid(name) && (
        <small className="p-error">{formik.errors[name]}</small>
      )
    )
  }

  // check if items are modified
  function getConfirmationCode(
    formData: FormValues,
  ): DespatchAdviceItem['ConfirmationCode'] {
    if (confirmationCode === 'REJECTED') return 'REJECTED'

    const comparisionArray: ComparisonArray = [
      {
        order: despatchAdviceItem?.ItemId,
        confirmation: formData.confirmationItemId,
      },
      {
        order: despatchAdviceItem?.CountryOfOrigin,
        confirmation: formData.confirmationCountryOfOrigin,
      },
      {
        order: despatchAdviceItem?.SellerProductId,
        confirmation: formData.confirmationSellerProductId,
      },
      {
        order: despatchAdviceItem?.SellerProductDescription,
        confirmation: formData.confirmationSellerProductDescription,
      },
      {
        order: despatchAdviceItem?.Quantity,
        confirmation: formData.confirmationQuantity,
      },
      {
        order: despatchAdviceItem?.Unit,
        confirmation: formData.confirmationUnit,
      },
      {
        order: despatchAdviceItem?.PriceUnitOfMeasure,
        confirmation: formData.confirmationPriceUnitOfMeasure,
      },
    ]

    return areItemsModified(comparisionArray) ? 'MODIFIED' : 'NOT_SET'
  }

  // submit form
  const submitForm = async (formData: FormValues) => {
    // new despatch advice item
    const newData: DespatchAdviceItem = produce(despatchAdviceItem!, draft => {
      draft.ConfirmationItemId = formData.confirmationItemId
      draft.ConfirmationCountryOfOrigin = formData.confirmationCountryOfOrigin
      draft.ConfirmationSellerProductId = formData.confirmationSellerProductId
      draft.ConfirmationSellerProductDescription =
        formData.confirmationSellerProductDescription
      draft.ConfirmationQuantity = formData.confirmationQuantity as number
      draft.DeliveryStatus = formData.deliveryStatus
      draft.ConfirmationUnit = formData.confirmationUnit as string
      draft.ConfirmationPriceUnitOfMeasure =
        formData.confirmationPriceUnitOfMeasure
      draft.ConfirmationCode = getConfirmationCode(formData)

      // Remove rejection if there was any
      if (draft?.ConfirmationReason) {
        draft.ConfirmationReason.Code = undefined
        draft.ConfirmationReason.Description = undefined
      }
    })

    // patch for ConfirmationCode
    const newDataArray: DespatchAdviceItem[] =
      despatchAdviceItems?.map(item =>
        item.Id === newData.Id ? newData : item,
      ) || []

    const notSetCode: DespatchAdvice['ConfirmationCode'] = 'NOT_SET'
    const modifiedCode: DespatchAdvice['ConfirmationCode'] = 'MODIFIED'
    const patchData: PatchRequest<WebEdiDto<DespatchAdvice>> = [
      {
        op: 'replace',
        path: '/Payload/ConfirmationCode',
        value: isOrderModified(newDataArray) ? modifiedCode : notSetCode,
      },
    ]

    // patch for despatch advice
    await requestPatchDespatchAdviceById(id, patchData)
    await requestPutDespatchAdviceItemById(id, itemId!, newData)
  }

  const handleReset = async () => {
    await formik.setFieldValue('confirmationItemId', despatchAdviceItem?.ItemId)
    await formik.setFieldValue(
      'confirmationCountryOfOrigin',
      despatchAdviceItem?.CountryOfOrigin,
    )
    await formik.setFieldValue(
      'confirmationSellerProductId',
      despatchAdviceItem?.SellerProductId,
    )
    await formik.setFieldValue(
      'confirmationSellerProductDescription',
      despatchAdviceItem?.SellerProductDescription,
    )
    await formik.setFieldValue(
      'confirmationQuantity',
      despatchAdviceItem?.Quantity!,
    )
    await formik.setFieldValue('deliveryStatus', 'NOT_SET')
    await formik.setFieldValue('confirmationUnit', despatchAdviceItem?.Unit)
    await formik.setFieldValue(
      'confirmationPriceBaseQuantity',
      despatchAdviceItem?.PriceBaseQuantity,
    )
    await formik.setFieldValue(
      'confirmationPriceUnitOfMeasure',
      despatchAdviceItem?.PriceUnitOfMeasure,
    )
    await formik.setFieldValue('confirmationPrice', despatchAdviceItem?.Price)
    await formik.setFieldValue(
      'confirmationPriceSum',
      despatchAdviceItem?.PriceSum,
    )
    await formik.setFieldValue(
      'confirmationAllowanceChargeSum',
      despatchAdviceItem?.AllowanceChargeSum,
    )
    await formik.setFieldValue(
      'confirmationLineItemAmount',
      despatchAdviceItem?.LineItemAmount,
    )
    await formik.setFieldValue(
      'confirmationDeliveryDate',
      despatchAdviceItem?.DeliveryDate
        ? new Date(despatchAdviceItem.DeliveryDate)
        : undefined,
    )

    setConfirmationCode('NOT_SET')
  }

  // handleClose
  const handleClose = () => {
    formik.resetForm()
    setConfirmationCode(despatchAdviceItem?.ConfirmationCode)
    setVisible(false)
  }

  // ************************************************************
  // components
  // ************************************************************
  // itemId
  const itemIdLabel = <label htmlFor="itemId" className="text-sm"></label>

  const itemIdComponent = (
    <InputText
      id="itemId"
      className="w-full"
      value={despatchAdviceItem?.ItemId}
      disabled
    />
  )

  const itemIdWrapper = (
    <ConfigurableComponent
      config={config.despatchAdvice.editDespatchAdviceItem}
      component={itemIdComponent}
      label={itemIdLabel}
      isLoading={isLoading}
    />
  )

  // confirmationItemId
  const confirmationItemIdLabel = (
    <label
      htmlFor="confirmationItemId"
      className={classNames('text-sm', {
        'p-error': !isFormFieldValid('confirmationItemId'),
      })}
    ></label>
  )

  const confirmationItemIdComponent = (
    <InputText
      id="confirmationItemId"
      value={formik.values.confirmationItemId}
      onChange={formik.handleChange}
      className={classNames('w-full', {
        'p-invalid': !isFormFieldValid('confirmationItemId'),
      })}
      disabled={getConfirmationCode(formik.values) === 'REJECTED'}
    />
  )

  const confirmationItemIdErrorMsg = (
    <div>{getFormErrorMessage('confirmationItemId')}</div>
  )

  const confirmationItemIdWrapper = (
    <ConfigurableComponent
      config={config.despatchAdvice.editDespatchAdviceItem}
      component={confirmationItemIdComponent}
      label={confirmationItemIdLabel}
      errorMsg={confirmationItemIdErrorMsg}
      isLoading={isLoading}
    />
  )

  // countryOfOrigin
  const countryOfOriginLabel = (
    <label htmlFor="countryOfOrigin" className="text-sm"></label>
  )

  const countryOfOriginComponent = (
    <InputText
      id="countryOfOrigin"
      className="w-full"
      value={despatchAdviceItem?.CountryOfOrigin}
      disabled
    />
  )

  const countryOfOriginWrapper = (
    <ConfigurableComponent
      config={config.despatchAdvice.editDespatchAdviceItem}
      component={countryOfOriginComponent}
      label={countryOfOriginLabel}
      isLoading={isLoading}
    />
  )

  // confirmationCountryOfOrigin
  const confirmationCountryOfOriginLabel = (
    <label
      htmlFor="confirmationCountryOfOrigin"
      className={classNames('text-sm', {
        'p-error': !isFormFieldValid('confirmationCountryOfOrigin'),
      })}
    ></label>
  )

  const confirmationCountryOfOriginComponent = (
    <InputText
      id="confirmationCountryOfOrigin"
      value={formik.values.confirmationCountryOfOrigin}
      onChange={formik.handleChange}
      className={classNames('w-full', {
        'p-invalid': !isFormFieldValid('confirmationCountryOfOrigin'),
      })}
      keyfilter="alpha"
      disabled={getConfirmationCode(formik.values) === 'REJECTED'}
    />
  )

  const confirmationCountryOfOriginErrorMsg = (
    <div>{getFormErrorMessage('confirmationCountryOfOrigin')}</div>
  )

  const confirmationCountryOfOriginWrapper = (
    <ConfigurableComponent
      config={config.despatchAdvice.editDespatchAdviceItem}
      component={confirmationCountryOfOriginComponent}
      label={confirmationCountryOfOriginLabel}
      errorMsg={confirmationCountryOfOriginErrorMsg}
      isLoading={isLoading}
    />
  )

  // gtin
  const gtinLabel = <label htmlFor="gtin" className="text-sm"></label>

  const gtinComponent = (
    <InputText
      id="gtin"
      className="w-full"
      value={despatchAdviceItem?.GTIN}
      disabled
    />
  )

  const gtinWrapper = (
    <ConfigurableComponent
      config={config.despatchAdvice.editDespatchAdviceItem}
      component={gtinComponent}
      label={gtinLabel}
      isLoading={isLoading}
    />
  )

  // buyerProductId
  const buyerProductIdLabel = (
    <label htmlFor="buyerProductId" className="text-sm"></label>
  )

  const buyerProductIdComponent = (
    <InputText
      id="buyerProductId"
      className="w-full"
      value={despatchAdviceItem?.BuyerProductId}
      disabled
    />
  )

  const buyerProductIdWrapper = (
    <ConfigurableComponent
      config={config.despatchAdvice.editDespatchAdviceItem}
      component={buyerProductIdComponent}
      label={buyerProductIdLabel}
      isLoading={isLoading}
    />
  )

  // confirmationSellerProductId
  const confirmationSellerProductIdLabel = (
    <label
      htmlFor="confirmationSellerProductId"
      className={classNames('text-sm', {
        'p-error': !isFormFieldValid('confirmationSellerProductId'),
      })}
    ></label>
  )

  const confirmationSellerProductIdComponent = (
    <InputText
      id="confirmationSellerProductId"
      value={formik.values.confirmationSellerProductId}
      onChange={formik.handleChange}
      className={classNames('w-full', {
        'p-invalid': !isFormFieldValid('confirmationSellerProductId'),
      })}
      disabled={getConfirmationCode(formik.values) === 'REJECTED'}
    />
  )

  const confirmationSellerProductIdErrorMsg = (
    <div>{getFormErrorMessage('confirmationSellerProductId')}</div>
  )

  const confirmationSellerProductIdWrapper = (
    <ConfigurableComponent
      config={config.despatchAdvice.editDespatchAdviceItem}
      component={confirmationSellerProductIdComponent}
      label={confirmationSellerProductIdLabel}
      errorMsg={confirmationSellerProductIdErrorMsg}
      isLoading={isLoading}
    />
  )

  // buyerProductDescription
  const buyerProductDescriptionLabel = (
    <label htmlFor="buyerProductDescription" className="text-sm"></label>
  )

  const buyerProductDescriptionComponent = (
    <InputText
      id="buyerProductDescription"
      className="w-full"
      value={despatchAdviceItem?.BuyerProductDescription}
      disabled
    />
  )

  const buyerProductDescriptionWrapper = (
    <ConfigurableComponent
      config={config.despatchAdvice.editDespatchAdviceItem}
      component={buyerProductDescriptionComponent}
      label={buyerProductDescriptionLabel}
      isLoading={isLoading}
    />
  )

  // confirmationSellerProductDescription
  const confirmationSellerProductDescriptionLabel = (
    <label
      htmlFor="confirmationSellerProductDescription"
      className={classNames('text-sm', {
        'p-error': !isFormFieldValid('confirmationSellerProductDescription'),
      })}
    ></label>
  )

  const confirmationSellerProductDescriptionComponent = (
    <InputText
      id="confirmationSellerProductDescription"
      value={formik.values.confirmationSellerProductDescription}
      onChange={formik.handleChange}
      className={classNames('w-full', {
        'p-invalid': !isFormFieldValid('confirmationSellerProductDescription'),
      })}
      disabled={getConfirmationCode(formik.values) === 'REJECTED'}
    />
  )

  const confirmationSellerProductDescriptionErrorMsg = (
    <div>{getFormErrorMessage('confirmationSellerProductDescription')}</div>
  )

  const confirmationSellerProductDescriptionWrapper = (
    <ConfigurableComponent
      config={config.despatchAdvice.editDespatchAdviceItem}
      component={confirmationSellerProductDescriptionComponent}
      label={confirmationSellerProductDescriptionLabel}
      errorMsg={confirmationSellerProductDescriptionErrorMsg}
      isLoading={isLoading}
    />
  )

  //  quantity
  const quantityLabel = <label htmlFor="quantity" className="text-sm"></label>

  const quantityComponent = (
    <InputNumber
      id="quantity"
      className="w-full"
      value={despatchAdviceItem?.Quantity}
      locale={language}
      disabled
    />
  )

  const quantityWrapper = (
    <ConfigurableComponent
      config={config.despatchAdvice.editDespatchAdviceItem}
      component={quantityComponent}
      label={quantityLabel}
      isLoading={isLoading}
    />
  )

  // openQuantity
  function getOpenQuantity() {
    const openQuantity = despatchAdviceItem?.OpenQuantity ?? 0
    const confirmedQuantity = formik.values.confirmationQuantity ?? 0

    return openQuantity - confirmedQuantity
  }

  const openQuantityLabel = (
    <label htmlFor="openQuantity" className="text-sm"></label>
  )

  const openQuantityComponent = (
    <InputNumber
      id="openQuantity"
      className="w-full"
      value={despatchAdviceItem?.OpenQuantity}
      locale={language}
      disabled
    />
  )

  const openQuantityWrapper = (
    <ConfigurableComponent
      config={config.despatchAdvice.editDespatchAdviceItem}
      component={openQuantityComponent}
      label={openQuantityLabel}
      isLoading={isLoading}
    />
  )

  // confirmationQuantity
  const confirmationQuantityLabel = (
    <label
      htmlFor="confirmationQuantity"
      className={classNames('text-sm', {
        'p-error': !isFormFieldValid('confirmationQuantity'),
      })}
    ></label>
  )

  const updateConfirmationPriceSum = async (amount: number, price: number) => {
    setIsCalculating(true)
    await formik.setFieldValue('confirmationPriceSum', amount * price)
    await delay(1)
    setIsCalculating(false)
  }

  const onConfirmationQuantityChange = async (
    e: InputNumberValueChangeEvent,
  ) => {
    await formik.setFieldValue('confirmationQuantity', e.value)

    const amount = e.value || 0
    const price = formik.values.confirmationPrice || 0

    if (e.value) {
      await updateConfirmationPriceSum(amount, price)
    }
  }

  const confirmationQuantityComponent = (
    <InputNumber
      id="confirmationQuantity"
      value={formik.values.confirmationQuantity}
      onChange={(e: any) => {
        onConfirmationQuantityChange(e)
        formik.setFieldValue('deliveryStatus', 'NOT_SET')
      }}
      className={classNames('w-full', {
        'p-invalid': !isFormFieldValid('confirmationQuantity'),
      })}
      min={0}
      max={despatchAdviceItem?.Quantity}
      disabled={getConfirmationCode(formik.values) === 'REJECTED'}
      locale={language}
      aria-describedby="open-quantity"
    />
  )

  const confirmationQuantityErrorMsg = (
    <div>{getFormErrorMessage('confirmationQuantity')}</div>
  )

  const confirmationQuantityWrapper = (
    <ConfigurableComponent
      config={config.despatchAdvice.editDespatchAdviceItem}
      component={confirmationQuantityComponent}
      label={confirmationQuantityLabel}
      errorMsg={confirmationQuantityErrorMsg}
      className="mb-1"
      isLoading={isLoading}
    />
  )

  // deliveryStatus
  const deliveryStatusLabel = (
    <label
      htmlFor="deliveryStatus"
      className={classNames('text-sm', {
        'p-error': !isFormFieldValid('deliveryStatus'),
      })}
    ></label>
  )

  useSelectFirstAndOnlyOption(
    'deliveryStatus',
    config.despatchAdvice.editDespatchAdviceItem.deliveryStatus.options,
    formik,
  )

  const deliveryStatusComponent = (
    <Dropdown
      id="deliveryStatus"
      className={classNames('w-full', {
        'p-invalid': !isFormFieldValid('deliveryStatus'),
      })}
      value={formik.values.deliveryStatus}
      onChange={formik.handleChange}
      optionLabel="code"
      optionValue="code"
      disabled={getConfirmationCode(formik.values) === 'REJECTED'}
    />
  )

  const deliveryStatusErrorMsg = (
    <div>{getFormErrorMessage('deliveryStatus')}</div>
  )

  const deliveryStatusWrapper = (
    <ConfigurableComponent
      config={config.despatchAdvice.editDespatchAdviceItem}
      component={deliveryStatusComponent}
      label={deliveryStatusLabel}
      errorMsg={deliveryStatusErrorMsg}
      isLoading={isLoading}
    />
  )

  // unit
  const unitLabel = <label htmlFor="unit" className="text-sm"></label>

  const unitComponent = (
    <InputText
      id="unit"
      className="w-full"
      value={despatchAdviceItem?.Unit}
      disabled
    />
  )

  const unitWrapper = (
    <ConfigurableComponent
      config={config.despatchAdvice.editDespatchAdviceItem}
      component={unitComponent}
      label={unitLabel}
      isLoading={isLoading}
    />
  )

  //  confirmationUnit
  const confirmationUnitLabel = (
    <label
      htmlFor="confirmationUnit"
      className={classNames('text-sm', {
        'p-error': !isFormFieldValid('confirmationUnit'),
      })}
    ></label>
  )

  const confirmationUnitComponent = (
    <InputText
      id="confirmationUnit"
      value={formik.values.confirmationUnit}
      onChange={formik.handleChange}
      className={classNames('w-full', {
        'p-invalid': !isFormFieldValid('confirmationUnit'),
      })}
      disabled={getConfirmationCode(formik.values) === 'REJECTED'}
    />
  )

  const confirmationUnitErrorMsg = (
    <div>{getFormErrorMessage('confirmationUnit')}</div>
  )

  const confirmationUnitWrapper = (
    <ConfigurableComponent
      config={config.despatchAdvice.editDespatchAdviceItem}
      component={confirmationUnitComponent}
      label={confirmationUnitLabel}
      errorMsg={confirmationUnitErrorMsg}
      isLoading={isLoading}
    />
  )

  // priceBaseQuantity
  const priceBaseQuantityLabel = (
    <label htmlFor="priceBaseQuantity" className="text-sm"></label>
  )

  const priceBaseQuantityComponent = (
    <InputNumber
      id="priceBaseQuantity"
      className="w-full"
      value={despatchAdviceItem?.PriceBaseQuantity}
      locale={language}
      disabled
    />
  )

  const priceBaseQuantityWrapper = (
    <ConfigurableComponent
      config={config.despatchAdvice.editDespatchAdviceItem}
      component={priceBaseQuantityComponent}
      label={priceBaseQuantityLabel}
      isLoading={isLoading}
    />
  )

  // confirmationPriceBaseQuantity
  const confirmationPriceBaseQuantityLabel = (
    <label
      htmlFor="confirmationPriceBaseQuantity"
      className={classNames('text-sm', {
        'p-error': !isFormFieldValid('confirmationPriceBaseQuantity'),
      })}
    ></label>
  )

  const confirmationPriceBaseQuantityComponent = (
    <InputNumber
      id="confirmationPriceBaseQuantity"
      value={formik.values.confirmationPriceBaseQuantity}
      onChange={e =>
        formik.setFieldValue('confirmationPriceBaseQuantity', e.value)
      }
      className={classNames('w-full', {
        'p-invalid': !isFormFieldValid('confirmationPriceBaseQuantity'),
      })}
      locale={language}
      disabled={getConfirmationCode(formik.values) === 'REJECTED'}
    />
  )

  const confirmationPriceBaseQuantityErrorMsg = (
    <div>{getFormErrorMessage('confirmationPriceBaseQuantity')}</div>
  )

  const confirmationPriceBaseQuantityWrapper = (
    <ConfigurableComponent
      config={config.despatchAdvice.editDespatchAdviceItem}
      component={confirmationPriceBaseQuantityComponent}
      label={confirmationPriceBaseQuantityLabel}
      errorMsg={confirmationPriceBaseQuantityErrorMsg}
      isLoading={isLoading}
    />
  )

  // priceUnitOfMeasure
  const priceUnitOfMeasureLabel = (
    <label htmlFor="priceUnitOfMeasure" className="text-sm"></label>
  )

  const priceUnitOfMeasureComponent = (
    <InputText
      id="priceUnitOfMeasure"
      className="w-full"
      value={despatchAdviceItem?.PriceUnitOfMeasure}
      disabled
    />
  )

  const priceUnitOfMeasureWrapper = (
    <ConfigurableComponent
      config={config.despatchAdvice.editDespatchAdviceItem}
      component={priceUnitOfMeasureComponent}
      label={priceUnitOfMeasureLabel}
      isLoading={isLoading}
    />
  )

  // confirmationPriceUnitOfMeasure
  const confirmationPriceUnitOfMeasureLabel = (
    <label
      htmlFor="confirmationPriceUnitOfMeasure"
      className={classNames('text-sm', {
        'p-error': !isFormFieldValid('confirmationPriceUnitOfMeasure'),
      })}
    ></label>
  )

  const confirmationPriceUnitOfMeasureComponent = (
    <InputText
      id="confirmationPriceUnitOfMeasure"
      value={formik.values.confirmationPriceUnitOfMeasure}
      onChange={formik.handleChange}
      className={classNames('w-full', {
        'p-invalid': !isFormFieldValid('confirmationPriceUnitOfMeasure'),
      })}
      disabled={getConfirmationCode(formik.values) === 'REJECTED'}
    />
  )

  const confirmationPriceUnitOfMeasureErrorMsg = (
    <div>{getFormErrorMessage('confirmationPriceUnitOfMeasure')}</div>
  )

  const confirmationPriceUnitOfMeasureWrapper = (
    <ConfigurableComponent
      config={config.despatchAdvice.editDespatchAdviceItem}
      component={confirmationPriceUnitOfMeasureComponent}
      label={confirmationPriceUnitOfMeasureLabel}
      errorMsg={confirmationPriceUnitOfMeasureErrorMsg}
      isLoading={isLoading}
    />
  )

  // price
  const priceLabel = <label htmlFor="price" className="text-sm"></label>
  const priceComponent = (
    <InputNumber
      id="price"
      value={despatchAdviceItem?.Price}
      mode="currency"
      currency={currency}
      className="w-full"
      locale={language}
      disabled
    />
  )

  const priceWrapper = (
    <ConfigurableComponent
      config={config.despatchAdvice.editDespatchAdviceItem}
      component={priceComponent}
      label={priceLabel}
      isLoading={isLoading}
    />
  )

  // confirmationPrice
  const confirmationPriceLabel = (
    <label
      htmlFor="confirmationPrice"
      className={classNames('text-sm', {
        'p-error': !isFormFieldValid('confirmationPrice'),
      })}
    ></label>
  )

  const onConfirmationPriceChange = async (e: InputNumberValueChangeEvent) => {
    await formik.setFieldValue('confirmationPrice', e.value)

    const amount = formik.values.confirmationQuantity || 0
    const price = e.value || 0

    await updateConfirmationPriceSum(amount, price)
  }

  const confirmationPriceComponent = (
    <InputNumber
      id="confirmationPrice"
      value={formik.values.confirmationPrice}
      onChange={(e: any) => onConfirmationPriceChange(e)}
      mode="currency"
      currency={currency}
      className={classNames('w-full', {
        'p-invalid': !isFormFieldValid('confirmationPrice'),
      })}
      locale={language}
      disabled={getConfirmationCode(formik.values) === 'REJECTED'}
    />
  )

  const confirmationPriceErrorMsg = (
    <div>{getFormErrorMessage('confirmationPrice')}</div>
  )

  const confirmationPriceWrapper = (
    <ConfigurableComponent
      config={config.despatchAdvice.editDespatchAdviceItem}
      component={confirmationPriceComponent}
      label={confirmationPriceLabel}
      errorMsg={confirmationPriceErrorMsg}
      isLoading={isLoading}
    />
  )

  // priceSum
  const priceSumLabel = <label htmlFor="priceSum" className="text-sm"></label>

  const priceSumComponent = (
    <InputNumber
      id="priceSum"
      value={despatchAdviceItem?.PriceSum}
      mode="currency"
      currency={currency}
      className="w-full"
      locale={language}
      disabled
    />
  )

  const priceSumWrapper = (
    <ConfigurableComponent
      config={config.despatchAdvice.editDespatchAdviceItem}
      component={priceSumComponent}
      label={priceSumLabel}
      isLoading={isLoading}
    />
  )

  // confirmationPriceSum
  const confirmationPriceSumLabel = (
    <label
      htmlFor="confirmationPriceSum"
      className={classNames('text-sm', {
        'p-error': !isFormFieldValid('confirmationPriceSum'),
      })}
    >
      <Tooltip target="#custom-target-icon" />
      <i
        id="custom-target-icon"
        className=" pi pi-info-circle  ml-1 text-xs"
        data-pr-tooltip={t('common.tooltips.calculating')}
        data-pr-position="right"
        data-pr-at="right+5 center"
      />
    </label>
  )

  const confirmationPriceSumComponent = (
    <InputNumber
      id="confirmationPriceSum"
      mode="currency"
      currency={currency}
      value={formik.values.confirmationPriceSum}
      onChange={e => formik.setFieldValue('confirmationPriceSum', e.value)}
      className={classNames('w-full', {
        'p-invalid': !isFormFieldValid('confirmationPriceSum'),
      })}
      locale={language}
      disabled={getConfirmationCode(formik.values) === 'REJECTED'}
    />
  )

  const confirmationPriceSumErrorMsg = (
    <div>{getFormErrorMessage('confirmationPriceSum')}</div>
  )

  const confirmationPriceSumWrapper = (
    <ConfigurableComponent
      config={config.despatchAdvice.editDespatchAdviceItem}
      component={confirmationPriceSumComponent}
      label={confirmationPriceSumLabel}
      errorMsg={confirmationPriceSumErrorMsg}
      isCalculating={isCalculating}
      isLoading={isLoading}
    />
  )

  // allowanceChargeSum
  const allowanceChargeSumLabel = (
    <label htmlFor="allowanceChargeSum" className="text-sm"></label>
  )

  const allowanceChargeSumComponent = (
    <InputNumber
      id="allowanceChargeSum"
      value={despatchAdviceItem?.AllowanceChargeSum}
      mode="currency"
      currency={currency}
      className="w-full"
      locale={language}
      disabled
    />
  )

  const allowanceChargeSumWrapper = (
    <ConfigurableComponent
      config={config.despatchAdvice.editDespatchAdviceItem}
      component={allowanceChargeSumComponent}
      label={allowanceChargeSumLabel}
      isLoading={isLoading}
    />
  )

  // confirmationAllowanceChargeSum
  const confirmationAllowanceChargeSumLabel = (
    <label
      htmlFor="confirmationAllowanceChargeSum"
      className={classNames('text-sm', {
        'p-error': !isFormFieldValid('confirmationAllowanceChargeSum'),
      })}
    ></label>
  )

  const confirmationAllowanceChargeSumComponent = (
    <InputNumber
      id="confirmationAllowanceChargeSum"
      mode="currency"
      currency={currency}
      value={formik.values.confirmationAllowanceChargeSum}
      onChange={e =>
        formik.setFieldValue('confirmationAllowanceChargeSum', e.value)
      }
      className={classNames('w-full', {
        'p-invalid': !isFormFieldValid('confirmationAllowanceChargeSum'),
      })}
      locale={language}
      disabled={getConfirmationCode(formik.values) === 'REJECTED'}
    />
  )

  const confirmationAllowanceChargeSumErrorMsg = (
    <div>{getFormErrorMessage('confirmationAllowanceChargeSum')}</div>
  )

  const confirmationAllowanceChargeSumWrapper = (
    <ConfigurableComponent
      config={config.despatchAdvice.editDespatchAdviceItem}
      component={confirmationAllowanceChargeSumComponent}
      label={confirmationAllowanceChargeSumLabel}
      errorMsg={confirmationAllowanceChargeSumErrorMsg}
      isLoading={isLoading}
    />
  )

  // lineItemAmount
  const lineItemAmountLabel = (
    <label htmlFor="lineItemAmount" className="text-sm"></label>
  )

  const lineItemAmountComponent = (
    <InputNumber
      id="lineItemAmount"
      value={despatchAdviceItem?.LineItemAmount}
      mode="currency"
      currency={currency}
      className="w-full"
      locale={language}
      disabled
    />
  )

  const lineItemAmountWrapper = (
    <ConfigurableComponent
      config={config.despatchAdvice.editDespatchAdviceItem}
      component={lineItemAmountComponent}
      label={lineItemAmountLabel}
      isLoading={isLoading}
    />
  )

  // confirmationLineItemAmount
  const confirmationLineItemAmountLabel = (
    <label
      htmlFor="confirmationLineItemAmount"
      className={classNames('text-sm', {
        'p-error': !isFormFieldValid('confirmationLineItemAmount'),
      })}
    ></label>
  )

  const confirmationLineItemAmountComponent = (
    <InputNumber
      id="confirmationLineItemAmount"
      mode="currency"
      currency={currency}
      value={formik.values.confirmationLineItemAmount}
      onChange={e =>
        formik.setFieldValue('confirmationLineItemAmount', e.value)
      }
      className={classNames('w-full', {
        'p-invalid': !isFormFieldValid('confirmationLineItemAmount'),
      })}
      locale={language}
      disabled={getConfirmationCode(formik.values) === 'REJECTED'}
    />
  )

  const confirmationLineItemAmountErrorMsg = (
    <div>{getFormErrorMessage('confirmationLineItemAmount')}</div>
  )

  const confirmationLineItemAmountWrapper = (
    <ConfigurableComponent
      config={config.despatchAdvice.editDespatchAdviceItem}
      component={confirmationLineItemAmountComponent}
      label={confirmationLineItemAmountLabel}
      errorMsg={confirmationLineItemAmountErrorMsg}
      isLoading={isLoading}
    />
  )

  // deliveryDate
  const deliveryDateLabel = (
    <label htmlFor="deliveryDate" className="text-sm"></label>
  )

  const deliveryDate = despatchAdviceItem?.DeliveryDate
    ? new Date(despatchAdviceItem?.DeliveryDate)
    : null

  const deliveryDateComponent = (
    <Calendar
      id="deliveryDate"
      value={deliveryDate}
      className="w-full"
      showIcon
      locale={language}
      disabled
    />
  )

  const deliveryDateWrapper = (
    <ConfigurableComponent
      config={config.despatchAdvice.editDespatchAdviceItem}
      component={deliveryDateComponent}
      label={deliveryDateLabel}
      isLoading={isLoading}
    />
  )

  // confirmationDeliveryDate
  const confirmationDeliveryDateLabel = (
    <label
      htmlFor="confirmationDeliveryDate"
      className={classNames('text-sm', {
        'p-error': !isFormFieldValid('confirmationDeliveryDate'),
      })}
    ></label>
  )

  const confirmationDeliveryDateComponent = (
    <Calendar
      id="confirmationDeliveryDate"
      value={formik.values.confirmationDeliveryDate}
      onChange={formik.handleChange}
      className={classNames('w-full', {
        'p-invalid': !isFormFieldValid('confirmationDeliveryDate'),
      })}
      showButtonBar
      showIcon
      minDate={new Date()}
      dateTemplate={(date: any) => (
        <DateTemplate
          date={date}
          desiredDeliveryDate={despatchAdviceItem?.DeliveryDate}
        />
      )}
      locale={language}
      disabled={getConfirmationCode(formik.values) === 'REJECTED'}
    />
  )

  const confirmationDeliveryDateErrorMsg = (
    <div>{getFormErrorMessage('confirmationDeliveryDate')}</div>
  )

  const confirmationDeliveryDateWrapper = (
    <ConfigurableComponent
      config={config.despatchAdvice.editDespatchAdviceItem}
      component={confirmationDeliveryDateComponent}
      label={confirmationDeliveryDateLabel}
      errorMsg={confirmationDeliveryDateErrorMsg}
      isLoading={isLoading}
    />
  )

  // ************************************************************
  // dialog components
  // ************************************************************
  // header
  const header = (
    <>
      <div className="flex align-content-center flex-wrap">
        <span className="mr-3">
          {t('common.titles.editItem', {
            position: despatchAdviceItem?.OrderPosition,
          })}
        </span>

        <StatusTag status={getConfirmationCode(formik.values)} />
      </div>
    </>
  )

  // footer
  const isModified = formik.dirty
  const isReset =
    despatchAdviceItem?.ConfirmationCode === 'REJECTED' &&
    confirmationCode === 'NOT_SET'
  const isDisabled = !(isModified || isReset)

  const footer = (
    <div className="flex flex-wrap justify-content-between pt-3">
      <Button
        className="p-button-text w-full md:w-auto"
        label={t('common.buttons.reset')}
        icon="pi pi-replay"
        onClick={handleReset}
        type="reset"
        disabled={
          !(
            getConfirmationCode(formik.values) === 'MODIFIED' ||
            getConfirmationCode(formik.values) === 'REJECTED'
          )
        }
      />

      <Button
        className="p-button-text ml-auto w-full md:w-auto mb-1 md:mb-0"
        label={t('common.buttons.cancel')}
        icon="pi pi-times"
        onClick={handleClose}
        type="button"
      />

      <Button
        label={t('common.buttons.save')}
        className="w-full md:w-auto"
        icon={'pi pi-check'}
        type="submit"
        onClick={formik.submitForm}
        disabled={isDisabled}
        loading={isRequestRunning}
      />
    </div>
  )

  return (
    <Dialog
      header={header}
      visible={visible}
      style={{ width: '55rem' }}
      breakpoints={{ [calculateBreakpointWidth(55)]: '90vw' }}
      footer={footer}
      onHide={handleClose}
      blockScroll
    >
      {/* itemId */}
      <div className="formgrid grid">
        <div className="col-12 md:col-4">{itemIdWrapper}</div>
        <div className="col-12 md:col-4">{confirmationItemIdWrapper}</div>
      </div>

      <Divider className="md:hidden" />

      {/* countryOfOrigin */}
      <div className="formgrid grid">
        <div className="col-12 md:col-4">{countryOfOriginWrapper}</div>
        <div className="col-12 md:col-4">
          {confirmationCountryOfOriginWrapper}
        </div>
      </div>

      <Divider className="md:hidden" />

      {/* gtin */}
      <div className="formgrid grid">
        <div className="col-12 md:col-4">{gtinWrapper}</div>
      </div>

      <Divider className="md:hidden" />

      {/* buyerProductId */}
      <div className="formgrid grid">
        <div className="col-12 md:col-4">{buyerProductIdWrapper}</div>
        <div className="col-12 md:col-4">
          {confirmationSellerProductIdWrapper}
        </div>
      </div>

      <Divider className="md:hidden" />

      {/* buyerProductDescription */}
      <div className="formgrid grid">
        <div className="col-12 md:col-4">{buyerProductDescriptionWrapper}</div>

        <div className="col-12 md:col-4">
          {confirmationSellerProductDescriptionWrapper}
        </div>
      </div>

      <Divider className="md:hidden" />

      {/* quantity */}
      <div className="formgrid grid">
        <div className="col-12 md:col-4">{quantityWrapper}</div>
      </div>

      {/* openQuantity, confirmationQuantity, deliveryStatus  */}
      <div className="formgrid grid">
        <div className="col-12 md:col-4">{openQuantityWrapper}</div>
        <div className="col-12 md:col-4">
          {confirmationQuantityWrapper}
          {/* only show this help text if the open quantity is bigger than 0 */}
          {getOpenQuantity() > 0 && (
            <small id="open-quantity" className="block mb-3">
              {t(
                'despatchAdvice.positions.table.edit.dialog.form.confirmationQuantity.help.openQuantity',
                {
                  openQuantity: getOpenQuantity().toLocaleString(language),
                },
              )}
            </small>
          )}
        </div>
        {/* only show this field if the open quantity is bigger than 0 */}
        {getOpenQuantity() > 0 && (
          <div className="col-12 md:col-4">{deliveryStatusWrapper}</div>
        )}
      </div>

      <Divider className="md:hidden" />

      {/* unit */}
      <div className="formgrid grid">
        <div className="col-12 md:col-4">{unitWrapper}</div>
        <div className="col-12 md:col-4">{confirmationUnitWrapper}</div>
      </div>

      <Divider className="md:hidden" />

      {/* priceBaseQuantity */}
      <div className="formgrid grid">
        <div className="col-12 md:col-4">{priceBaseQuantityWrapper}</div>

        <div className="col-12 md:col-4">
          {confirmationPriceBaseQuantityWrapper}
        </div>
      </div>

      <Divider className="md:hidden" />

      {/* priceUnitOfMeasure */}
      <div className="formgrid grid">
        <div className="col-12 md:col-4">{priceUnitOfMeasureWrapper}</div>
        <div className="col-12 md:col-4">
          {confirmationPriceUnitOfMeasureWrapper}
        </div>
      </div>

      <Divider className="md:hidden" />

      {/* price */}
      <div className="formgrid grid">
        <div className="col-12 md:col-4">{priceWrapper}</div>

        <div className="col-12 md:col-4">{confirmationPriceWrapper}</div>
      </div>

      <Divider className="md:hidden" />

      {/* confirmationPriceSum */}
      <div className="formgrid grid">
        <div className="col-12 md:col-4">{priceSumWrapper}</div>

        <div className="col-12 md:col-4">{confirmationPriceSumWrapper}</div>
      </div>

      <Divider className="md:hidden" />

      {/* allowanceChargeSum */}
      <div className="formgrid grid">
        <div className="col-12 md:col-4">{allowanceChargeSumWrapper}</div>

        <div className="col-12 md:col-4">
          {confirmationAllowanceChargeSumWrapper}
        </div>
      </div>

      <Divider className="md:hidden" />

      {/* ItemAmount */}
      <div className="formgrid grid">
        <div className="col-12 md:col-4">{lineItemAmountWrapper}</div>

        <div className="col-12 md:col-4">
          {confirmationLineItemAmountWrapper}
        </div>
      </div>

      <Divider className="md:hidden" />

      {/* deliveryDate */}
      <div className="formgrid grid">
        <div className="col-12 md:col-4">{deliveryDateWrapper}</div>

        <div className="col-12 md:col-4">{confirmationDeliveryDateWrapper}</div>
      </div>
    </Dialog>
  )
}
